/** @format */

"use client";

import { ImageLoader } from "@/components/ui/image-loader";
import { CategoryInterface } from "@/types/category";
import { ProductInterface } from "@/types/product";
import { PromotionInterface } from "@/types/promotion";
import Link from "next/link";
import { ArrowRight, Star, TrendingUp, Package, Tag } from "lucide-react";
import { Slider } from "@/common/SliderCustom";
import { ProductCard } from "@/components/ui/product/product-card";
import { useWindowSize } from "@/hooks/useWindowSize";
import { useEffect, useState, useMemo } from "react";

interface ClientProps {
  products: ProductInterface[];
}

type TabType =
  | "Guitar Acoustic"
  | "Guitar Classic"
  | "Guitar Vọng Cổ"
  | "Phụ kiện";

const tabs: { key: TabType; label: string }[] = [
  { key: "Guitar Acoustic", label: "Guitar Acoustic" },
  { key: "Guitar Classic", label: "Guitar Classic" },
  { key: "Guitar Vọng Cổ", label: "Guitar Vọng Cổ" },
  { key: "Phụ kiện", label: "Phụ kiện" },
];

export const FeatureProductWithTabs = ({ products }: ClientProps) => {
  const windowSize = useWindowSize();
  const [activeTab, setActiveTab] = useState<TabType>("Guitar Acoustic");
  const [sliderKey, setSliderKey] = useState(0);

  // Filter products based on active tab
  const filteredProducts = useMemo(() => {
    if (!products || products.length === 0) return [];

    return products.filter((product) => {
      const productName = product.name?.toLowerCase() || "";
      const tabKey = activeTab.toLowerCase();
      return productName.includes(tabKey);
    });
  }, [products, activeTab]);

  // Take only 30 items
  const displayProducts = useMemo(() => {
    return filteredProducts.slice(0, 30);
  }, [filteredProducts]);

  // Calculate items per view based on window size
  const itemsPerView = useMemo(() => {
    if (windowSize.width < 640) return 2;
    if (windowSize.width < 768) return 3;
    if (windowSize.width < 1024) return 4;
    return 5;
  }, [windowSize.width]);

  // Force slider re-render when window size changes to fix the bug
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSliderKey((prev) => prev + 1);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [windowSize.width]);

  // Also force re-render when tab changes
  useEffect(() => {
    setSliderKey((prev) => prev + 1);
  }, [activeTab]);

  return (
    <section className={`py-3 sm:py-6 lg:py-8 bg-white`}>
      <div className="container mx-auto">
        {/* Featured Products Section */}

        {/* Tabs Section */}
        <div className="space-y-3">
          {/* Tab Navigation */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4 justify-center sm:justify-start">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                  activeTab === tab.key
                    ? "bg-black text-white shadow-lg"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Filtered Products */}
          {displayProducts.length > 0 ? (
            <div className="rounded-xl">
              <Slider
                key={`filtered-${activeTab}-${sliderKey}`}
                items={displayProducts}
                renderItem={(product) => <ProductCard product={product} />}
                onItemClick={(product, index) => {
                  console.log(`${activeTab} product clicked:`, product.name);
                }}
                itemsPerView={itemsPerView}
                gap={4}
                showArrows={windowSize.width >= 768}
                showDots={true}
                autoPlay={false} // Disable autoplay for filtered products
                className="w-full h-full"
              />
            </div>
          ) : (
            <div className="border border-stone-200 rounded-xl p-4 sm:p-8">
              <div className="text-center space-y-3">
                <div className="w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center mx-auto">
                  <Package className="w-6 h-6 text-stone-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-stone-800 mb-1">
                    Không có sản phẩm {activeTab}
                  </h3>
                  <p className="text-stone-500 text-sm max-w-md mx-auto">
                    Hiện tại không có sản phẩm nào thuộc danh mục "{activeTab}".
                    Vui lòng chọn tab khác hoặc quay lại sau.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Actions */}
        <div className="mt-4 sm:mt-6 space-y-3">
          <div className="max-w-[200px] mx-auto scale-80 sm:scale-100 ">
            <Link
              href={`/danh-muc/san-pham`}
              className="flex items-center justify-center gap-2 bg-black text-white px-4 py-3 rounded-lg font-medium transition-colors w-full">
              Xem thêm
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
